import { useMutation, useQueryClient } from '@tanstack/react-query';
import { enqueueSnackbar } from 'notistack';

import { getResolvedEndpoint } from '@/api/utils/getResolvedEndpoint';
import { handleApiCall } from '@/api/utils/handleApiCall';
import { useAuth } from '@/context/AuthProvider';

import { REPLIES_QUERY_KEY } from './useGetReplies';

export type ApiResponse_Toggle_Reply_Like = {
  id: number;
  liked: boolean;
  likeCount: number;
};

const METHOD = 'POST';
const ENDPOINT = 'posts/replies/{replyId}/like';

export const useToggleReplyLike = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      params: { replyId: number; postId: number; communityId?: number; groupId?: number }
    ) => {
      try {
        const { replyId } = params;
        const data = await handleApiCall<ApiResponse_Toggle_Reply_Like, Record<string, never>>(
          session,
          METHOD,
          getResolvedEndpoint(ENDPOINT, {
            replyId: replyId.toString(),
          }),
          {}
        );

        return data;
      } catch (err) {
        enqueueSnackbar('Failed to toggle like.', { variant: 'error' });
        throw err;
      }
    },
    onSuccess: (data, variables) => {
      // Invalidate post replies using correct query key
      queryClient.invalidateQueries({
        queryKey: REPLIES_QUERY_KEY({ 
          postId: variables.postId, 
          page: 1, 
          limit: 50 
        }),
      });
      
      // Invalidate all reply queries for this post
      queryClient.invalidateQueries({
        predicate: (query) => {
          const queryKey = query.queryKey as unknown[];
          const firstKey = queryKey[0];
          if (Array.isArray(firstKey) && typeof firstKey[1] === 'string') {
            return firstKey[1].includes(`posts/${variables.postId}/replies`);
          }
          return false;
        },
      });
    },
  });
};